import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {AuthenticationService} from '../services/authentication.service';
import {UserService} from '../../../services/user.service';
import 'rxjs/add/operator/map';
import {UserRoleEnum} from '../../../models/enum/user-role.enum';
import {Observable} from 'rxjs/Observable';
import {ConfigurationService} from '../../configuration/configuration.service';
import {DialogService} from '../../../services/dialog.service';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate, CanActivateChild {
  constructor(private authenticationService: AuthenticationService,
              private router: Router,
              private configurationService: ConfigurationService,
              private dialogService: DialogService,
              private userService: UserService) {
    RoleGuard.rg = this;
  }

  private static rg: RoleGuard;

  static checkAccess(route: string) {
    if (RoleGuard.rg) {
      RoleGuard.rg.userService.getUser(RoleGuard.rg.authenticationService.currentUserValue.authdata.userid).subscribe((userResponse) => {
        if (!userResponse) {
          return;
        }
        // Get all unique role ids from returned user groups.
        const rolesIds: string[] = [];
        for (const userInGroup of userResponse.cfgTDOUserInGroups) {
          for (const rls of userInGroup.cfgTDOUserGroup.cfgTDOUserGroupRoles) {
            if (!rolesIds.includes(rls.RoleId)) {
              rolesIds.push(rls.RoleId);
            }
          }
        }
        RoleGuard.rg.authenticationService.currentUserRolesIds = rolesIds;
        const hasAccess = RoleGuard.rg.checkEligibility(route);
        if (!hasAccess) {
          RoleGuard.rg.router.navigateByUrl('/access-denied');
        }
      });
    }
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let retVal = true;
    // return retVal; // This is to get back in easily if removed from a required group
    if (state.url.includes('/configuration/')) {
      if (this.configurationService.isConfigDirty) {
        return this.dialogService.openConfirmDialog('You have unsaved changes. Press ok to proceed and discard changes, or cancel so that you can save your changes.').map((res) => {
          if (!res) {
            return false;
          } else {
            this.configurationService.setClean();
            return true;
          }
        });
      }
    }

    if (this.authenticationService.currentUserRolesIds.length === 0) {

      return this.userService.getUser(this.authenticationService.currentUserValue?.authdata?.userid).map((userResponse) => {
        if (!userResponse) {
          return false;
        }
        // Get all unique role ids from returned user groups.
        const rolesIds: string[] = [];
        for (const userInGroup of userResponse.cfgTDOUserInGroups) {
          for (const rls of userInGroup.cfgTDOUserGroup.cfgTDOUserGroupRoles) {
            if (!rolesIds.includes(rls.RoleId)) {
              rolesIds.push(rls.RoleId);
            }
          }
        }
        this.authenticationService.currentUserRolesIds = rolesIds;
        retVal = this.handleRoute(route);
        if (!retVal) {
          this.router.navigateByUrl('/access-denied');
        }
        return retVal;
      });
    } else {
      retVal = this.handleRoute(route);
      if (!retVal) {
        this.router.navigateByUrl('/access-denied');
      }
      return retVal;
    }
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    let retVal = true;
    // return retVal; // This is to get back in easily if removed from a required group
    if (this.authenticationService.currentUserRolesIds.length === 0) {
      return this.userService.getUser(this.authenticationService.currentUserValue?.authdata?.userid).map((userResponse) => {
        if (!userResponse) {
          return false;
        }
        // Get all unique role ids from returned user groups.
        const rolesIds: string[] = [];
        for (const userInGroup of userResponse.cfgTDOUserInGroups) {
          for (const rls of userInGroup.cfgTDOUserGroup.cfgTDOUserGroupRoles) {
            if (!rolesIds.includes(rls.RoleId)) {
              rolesIds.push(rls.RoleId);
            }
          }
        }
        this.authenticationService.currentUserRolesIds = rolesIds;
        retVal = this.handleRoute(childRoute);
        if (!retVal) {
          this.router.navigateByUrl('/access-denied');
        }
        return retVal;
      });
    } else {
      retVal = this.handleRoute(childRoute);
      if (!retVal) {
        this.router.navigateByUrl('/access-denied');
      }
      return retVal;
    }
  }

  handleRoute(route: ActivatedRouteSnapshot) {
    const hasAccess = this.checkEligibility(route.url.toString());
    return hasAccess;
  }

  checkEligibility(route: string) {
    let routeToCheck = route;
    if (routeToCheck.includes('/')) {
      const idx = routeToCheck.lastIndexOf('/');
      routeToCheck = routeToCheck.substring(idx + 1, routeToCheck.length);
    }

    switch (routeToCheck) {
      case '':
        return true;
      case 'configuration':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Configuration)) {
          return true;
        }
        break;
      case 'practice-enterprise':
      case 'user-groups':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.AdministratorOfPractice)) {
          return true;
        }
        break;
      case 'enterprise-configuration':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.AdministratorOfEnterprise)) {
          return true;
        }
        break;
      case 'scheduler':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Scheduler)) {
          return true;
        }
        break;
      case 'patient-insurance':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Insurance) || this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.InsuranceViewOnly)) {
          return true;
        }
        break;
      case 'claims':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.InsuranceClaimsMgmt)) {
          return true;
        }
        break;
      case 'fees':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Fees)) {
          return true;
        }
        break;
      case 'ledger':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Ledger)) {
          return true;
        }
        break;
      case 'organizer':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.ImageOrganizer)) {
          return true;
        }
        break;
      case 'studio':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.ImagingStudio)) {
          return true;
        }
        break;
      case 'erx':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Erx)) {
          return true;
        }
        break;
      case 'treatment-plan':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.TreatmentPlanning)) {
          return true;
        }
        break;
      case 'visit':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.VisitWizard)) {
          return true;
        }
        break;
      case 'payment-audit':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.PaymentAudit)) {
          return true;
        }
        break;
      case 'insurance-payment':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.PaymentFromInsurance)) {
          return true;
        }
        break;
      case 'notes-to-front':
        if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.NoteToFront)) {
          return true;
        }
        break;
      case 'case-view':
          if (this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.CaseMgmtViewOnly) ||
              this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.CaseMgmt)) {
            return true;
          }
          break;
      default:
        return true;
    }

    return false;
  }
}
